import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { PlaylistTrack } from '@app/core/models/Track';
import { RadioWatcherService } from '@app/core/ngxs/radio-watcher.service';
import { DataResolved } from '@app/core/resolvers/generic-resolver.resolver';
import { distinctUntilChangedWithSideEffectOnLike } from '@app/core/rxjs/rxOperators';
import { PlaylistHeaderComponent } from '@app/library/components/content/playlist-header/playlist-header.component';
import { ColumnShown } from '@app/library/models/column-shown.model';
import { Playlist } from '@app/library/models/playlist.model';
import { SearchService } from '@app/library/services/search.service';
import { ColumnTypes } from '@app/library/states/columns.actions';
import { TogglePlaylistColumn } from '@app/library/states/playlist-columns.actions';
import { PlaylistColumnsState } from '@app/library/states/playlist-columns.state';
import {
  PlaylistTracksFailure,
  PlaylistTracksRequest,
  PlaylistTracksRequestNoLoader,
  PlaylistTracksSuccess,
} from '@app/library/states/playlist-tracks.actions';
import { PlaylistTracksState } from '@app/library/states/playlist-tracks.state';
import { PlaylistsRequest } from '@app/library/states/playlists.actions';
import { PlaylistsState } from '@app/library/states/playlists.state';
import { UnselectAll } from '@app/library/states/selected-tracks.actions';
import {
  TrackArchiveMultiBacSuccess,
  TrackArchiveSuccess,
} from '@app/library/states/tracks.actions';
import { Actions, ofActionDispatched, Select, Store } from '@ngxs/store';
import { UiStoreHelperService } from '@radioking/shared/common-services';
import {
  CloseCurrentPanel,
  PANELS_KEYS,
  PanelsState,
} from '@radioking/shared/pannel-manager';
import { isArray } from 'lodash';
import moment from 'moment-timezone';
import { BehaviorSubject, combineLatest, Observable, Subscription } from 'rxjs';
import {
  distinctUntilChanged,
  filter,
  map,
  shareReplay,
  switchMap,
  switchMapTo,
  take,
  tap,
} from 'rxjs/operators';

@Component({
  selector: 'rk-playlist-all',
  templateUrl: './playlist-all.component.html',
  styleUrls: ['./playlist-all.component.scss'],
})
export class PlaylistAllComponent implements OnInit, OnDestroy {
  playlist$: Observable<Playlist>;

  @Select(PlaylistColumnsState.getColumnShow)
  columns$: Observable<ColumnShown>;

  isProcessing$: Observable<boolean>;

  tracks$: Observable<PlaylistTrack[]>;

  isLoading$: Observable<boolean>;

  tracksUnfiltered$: Observable<PlaylistTrack[]>;

  searchSubject = new BehaviorSubject<string>('');

  subscription = new Subscription();

  @ViewChild('playlistHeader')
  header: PlaylistHeaderComponent;

  lastChangeOfTracks: moment.Moment | null = null;

  constructor(
    private readonly store: Store,
    private readonly route: ActivatedRoute,
    private readonly watcher: RadioWatcherService,
    private readonly actions$: Actions,
    private readonly searchService: SearchService,
    private readonly helper: UiStoreHelperService,
    private readonly router: Router,
  ) {}

  ngOnInit() {
    this.playlist$ = this.route.data.pipe(
      map<any, DataResolved<Playlist>>(val => val.playlist),
      tap(() => {
        if (this.header) {
          this.header.clearSearchBar();
          this.searchSubject.next('');
        }
      }),
      switchMap(val => val.data$),
      filter(val => typeof val !== 'undefined'),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.isLoading$ = this.helper.getLoadingState(
      PlaylistTracksRequest,
      [PlaylistTracksFailure, PlaylistTracksSuccess],
      true,
    );

    this.tracksUnfiltered$ = this.playlist$.pipe(
      switchMap(playlist =>
        this.store
          .select(PlaylistTracksState.playlistTracks)
          .pipe(map(fn => fn(playlist.id))),
      ),
      distinctUntilChanged(),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.tracks$ = this.searchService
      .createTracksSearch(this.tracksUnfiltered$, this.searchSubject.asObservable())
      .pipe(
        filter(val => isArray(val)),
        distinctUntilChangedWithSideEffectOnLike(tracks => {
          if (this.shouldUnselectAllWhenTracksChanged(tracks)) {
            this.store.dispatch(new UnselectAll());
          }
          this.lastChangeOfTracks = moment();
        }),
        shareReplay({ refCount: true, bufferSize: 1 }),
      );

    const tracksRequestSubscription = this.playlist$
      .pipe(
        map(playlist => playlist.id),
        distinctUntilChanged(),
        switchMap(id =>
          this.watcher.dispatchAndWatchAsObservable(new PlaylistTracksRequest(id)),
        ),
      )
      .subscribe(() => {});
    this.subscription.add(tracksRequestSubscription);

    this.subscription.add(
      combineLatest([
        this.actions$.pipe(
          ofActionDispatched(TrackArchiveSuccess, TrackArchiveMultiBacSuccess),
        ),
        this.playlist$,
      ]).subscribe(([, playlist]) => {
        this.store.dispatch(new PlaylistTracksRequestNoLoader(playlist.id));
      }),
    );

    this.isProcessing$ = this.playlist$.pipe(
      filter(pl => pl.type === 'dynamic'),
      switchMap(playlist =>
        this.store
          .select(PlaylistsState.playlistProcessing)
          .pipe(map(fn => fn(playlist.id))),
      ),
    );

    this.watchForProcessingChange();

    this.store.dispatch(new PlaylistsRequest(true));

    this.store.dispatch(new UnselectAll());
    this.subscription.add(
      this.router.events.subscribe(e => {
        if (e instanceof NavigationEnd) {
          this.store.dispatch(new UnselectAll());
        }
      }),
    );
  }

  watchForProcessingChange() {
    let isProcessStarted = false;

    const process = this.isProcessing$
      .pipe(
        distinctUntilChanged(),
        map<boolean, [boolean, boolean]>(val => {
          if (val) {
            isProcessStarted = true;

            return [val, true];
          } else {
            return [val, isProcessStarted];
          }
        }),
        map<[boolean, boolean], boolean>(([isProcessing, hasBeenProcessing]) => {
          if (hasBeenProcessing && !isProcessing) {
            isProcessStarted = false;

            return true;
          }

          return false;
        }),
        filter(val => val),
        switchMapTo(this.playlist$.pipe(take(1))),
      )
      .subscribe(playlist => {
        this.store.dispatch(new PlaylistTracksRequest(playlist.id));
      });

    this.subscription.add(process);
  }

  onChangeSearch(str: string) {
    if (typeof str === 'string') {
      this.searchSubject.next(str);
    }
  }

  onResetSort() {
    this.searchSubject.next('');
    this.header.clearSearchBar();
  }

  onColumnChange(column: ColumnTypes): void {
    this.store.dispatch(new TogglePlaylistColumn(column));
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
    const currentPanel = this.store.selectSnapshot(PanelsState.currentPanel);
    if (currentPanel === PANELS_KEYS.ADD_TO_PLAYLIST) {
      this.store.dispatch(new CloseCurrentPanel(true));
    }
  }

  /**
   * Track selection should be emptied when some tracks appear or disappear in list,
   * except when tracks appear from upload (new tracks).
   */
  shouldUnselectAllWhenTracksChanged(tracks: PlaylistTrack[]) {
    return (
      !this.lastChangeOfTracks ||
      !tracks.some(({ addedToLibraryDate }) =>
        addedToLibraryDate?.isAfter(this.lastChangeOfTracks),
      )
    );
  }
}
